import { renameClient, SubmitRenameClient } from '@/api/renameClient';
import i18n from '@/localisation/i18n';
import useAppStore from '@/store/app';
import { SimpleDialog } from '@/widgets/SimpleDialog';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

export const ItemButton = ({
  name,
  id,
  hasNew,
  isSelected,
  onDelete,
}: {
  name: string;
  id: string;
  hasNew: boolean;
  isSelected: boolean;
  onDelete: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [editName, setEditName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { client } = useParams();

  const [showDialog, setShowDialog] = useState(false);
  const { closeDrawer } = useAppStore();

  const { mutate } = useMutation({
    mutationFn: (data: SubmitRenameClient) => renameClient(data),
  });

  return (
    <>
      <SimpleDialog
        title={i18n.t('delete.client')}
        subtitle={i18n.t('delete.profile.subtitle')}
        actionButtonText={i18n.t('delete')}
        actionButtonClick={onDelete}
        closeDialog={() => setShowDialog(false)}
        showDialog={showDialog}
      />
      <Link
        className={clsx(
          'group mb-5 box-border flex items-center justify-between rounded-[10px] border border-transparent px-2 py-2',
          isSelected && 'bg-main',
          !isSelected && 'hover:border-[#42404D]'
        )}
        to={`/${id}`}
        onClick={() => {
          document.querySelector('main')?.scrollTo(0, 0);
          sessionStorage.removeItem(`smer_scroll_${client}`);

          setTimeout(() => {
            closeDrawer();
          }, 300);
        }}
      >
        {isEditing ? (
          <form
            className="w-full"
            onSubmit={e => {
              e.preventDefault();

              setIsEditing(false);

              mutate({ client: client!, name: editName });
            }}
          >
            <input
              value={editName}
              ref={inputRef}
              onBlur={() => {
                setIsEditing(false);

                mutate({ client: client!, name: editName });
              }}
              type="text"
              className="input input-bordered h-8 w-full px-2"
              placeholder={i18n.t('full.name')}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onChange={e => {
                setEditName(e.target.value);
              }}
            />
          </form>
        ) : (
          <div className="text-base font-semibold">{editName || name}</div>
        )}
        <div className="dropdown dropdown-end dropdown-bottom flex gap-2">
          {hasNew && (
            <div
              className={clsx(
                'h-2 w-2 shrink-0 self-center rounded-full bg-[var(--orange)] md:group-hover:hidden',
                showDialog && '!hidden'
              )}
            ></div>
          )}
          <>
            <div
              tabIndex={0}
              role="button"
              className={clsx(
                'md:hidden focus:md:block md:group-hover:block',
                isEditing && '!hidden'
              )}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <img
                className="h-6 w-6 shrink-0"
                src="/icons/more.svg"
                alt="more"
              />
            </div>
            <ul
              tabIndex={0}
              className="menu dropdown-content z-[1] mt-4 rounded-[10px] bg-[#42404D] p-2 shadow"
            >
              <li className="mb-1">
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    setShowDialog(true);
                  }}
                >
                  <img
                    className="h-5 w-5 min-w-5 shrink-0"
                    src="/icons/trash.svg"
                    alt="trash"
                  />
                  <span className="text-sm">{i18n.t('delete')}</span>
                </div>
              </li>
              <li>
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    setEditName(editName || name);
                    setIsEditing(true);

                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  }}
                >
                  <img
                    className="h-5 w-5 min-w-5 shrink-0"
                    src="/icons/edit.svg"
                    alt="edit"
                  />
                  <span className="text-sm">{i18n.t('edit')}</span>
                </div>
              </li>
            </ul>
          </>
        </div>
      </Link>
    </>
  );
};
